import { z } from 'zod';
/**
 * The chat socket protocol, shared by the backend handlers and the frontend.
 *
 * Requests are Socket.IO acknowledgements: the client emits a request with a
 * callback, and the server answers it exactly once with a `ChatAck` - the reply,
 * or why there is none. A failure (an invalid payload, a refusal, a server
 * error) travels in that answer and never as a socket `error`, so it reaches
 * the chat that asked, not the room connection. `ChatRequests` maps each
 * request event to its payload and reply.
 *
 * Events are only for what the server pushes unasked: `chat:initial_state`,
 * `chat:message` and `chat:activity`.
 *
 * Request payloads are Zod schemas: the server parses every one before it
 * touches the database. Replies and pushes are plain types: the server builds
 * them, so there is nothing to validate, only a shape both sides agree on.
 */
/**
 * Longest message the chat accepts, in UTF-16 code units (JS string length),
 * measured after trimming. `messages.content` is an unbounded TEXT column, so
 * this is a product limit rather than a storage one: the one the message input
 * has always shown, now enforced by the server as well.
 */
export const CHAT_MESSAGE_MAX_LENGTH = 2000;
/** Most messages a single `chat:get_messages` page may ask for. */
export const CHAT_MESSAGES_PAGE_MAX = 100;
// Channel ids, like message and user ids, are Postgres uuids
const ChannelIdSchema = z.string().uuid('Invalid channel ID');
// =============================================================================
// Requests (client to server, answered by an acknowledgement)
// =============================================================================
/**
 * `chat:send_message`: answered with the saved message. The sender pairs the
 * reply with its optimistic copy itself, so no client id is sent; any extra
 * field is stripped by the parse.
 */
export const ChatSendMessageRequestSchema = z.object({
    channelId: ChannelIdSchema,
    content: z
        .string()
        .trim()
        .min(1, 'Message cannot be empty')
        .max(CHAT_MESSAGE_MAX_LENGTH, `Message cannot be longer than ${CHAT_MESSAGE_MAX_LENGTH} characters`)
});
/** `chat:join_channel`: start viewing a channel; answered with its view. */
export const ChatJoinChannelRequestSchema = z.object({
    channelId: ChannelIdSchema
});
/** `chat:leave_channel`: stop viewing a channel (marks it read). Never refused. */
export const ChatLeaveChannelRequestSchema = z.object({
    channelId: ChannelIdSchema
});
/** `chat:start_dm`: open (creating if needed) a DM and start viewing it. */
export const ChatStartDmRequestSchema = z.object({
    targetUserId: z.string().uuid('Invalid user ID')
});
/**
 * `chat:get_messages`: a page of history older than the message `before`, or
 * the latest page without it. The server defaults `limit` when it is absent.
 */
export const ChatGetMessagesRequestSchema = z.object({
    channelId: ChannelIdSchema,
    before: z.string().uuid('Invalid message ID').optional(),
    limit: z.number().int().min(1).max(CHAT_MESSAGES_PAGE_MAX).optional()
});
